import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { offerApi, onboardingApi, referenceConsentApi } from '../services/api';
import ReferenceConsentForm from '../components/ReferenceConsentForm';
import { useToast } from '../components/Toast';
import { useConfirm } from '../components/ConfirmDialog';

const DOCUMENT_LIST = [
  { key: 'doc_diploma', label: '학력증명서', desc: '최종 학력 증명서', accept: '.pdf,.jpg,.jpeg,.png', formats: 'PDF, 이미지' },
  { key: 'doc_career', label: '경력확인서류', desc: '급여통장사본 또는 건강보험득실확인서 (택1)', accept: '.pdf,.jpg,.jpeg,.png', formats: 'PDF, 이미지' },
  { key: 'doc_salary_contract', label: '직전직장 연봉계약서', desc: '직전 직장의 연봉계약서', accept: '.pdf,.jpg,.jpeg,.png', formats: 'PDF, 이미지' },
  { key: 'doc_resident_copy', label: '주민등록등초본', desc: '주민등록등본 또는 초본', accept: '.pdf,.jpg,.jpeg,.png', formats: 'PDF, 이미지' },
  { key: 'doc_bank_account', label: '본인명의 통장사본', desc: '급여 수령용 통장사본', accept: '.pdf,.jpg,.jpeg,.png', formats: 'PDF, 이미지' },
  { key: 'doc_resume_kr', label: '이력서 한글본', desc: '한글 이력서', accept: '.doc,.docx,.pdf', formats: 'DOC, DOCX, PDF' },
  { key: 'doc_resume_en', label: '이력서 영문본', desc: '영문 이력서', accept: '.doc,.docx,.pdf', formats: 'DOC, DOCX, PDF' },
  { key: 'doc_profile_photo', label: '프로필 사진', desc: '흑백 여권사진 스타일', accept: '.jpg,.jpeg,.png', formats: 'JPG, PNG' },
  { key: 'doc_intro_ppt', label: '컨설턴트 소개자료', desc: 'PPT 형식 소개자료', accept: '.ppt,.pptx,.pdf', formats: 'PPT, PPTX, PDF' },
] as const;

interface ResultPageProps {
  applicant: any;
  onUpdate?: () => void;
}

const ResultPage: React.FC<ResultPageProps> = ({ applicant, onUpdate }) => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const confirm = useConfirm();
  const status = applicant?.status;

  const isOffer = status === 'OFFER';
  const isHired = status === 'HIRED';
  const isDocRejected = status === 'DOC_REJECTED';
  const isWrittenRejected = status === 'WRITTEN_REJECTED';
  const isRejected = status === 'REJECTED' || isDocRejected || isWrittenRejected;

  const [offer, setOffer] = useState<any>(null);
  const [offerLoading, setOfferLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [requestedSalary, setRequestedSalary] = useState<string>('');
  const [salarySubmitting, setSalarySubmitting] = useState(false);
  const [salarySaved, setSalarySaved] = useState(false);

  // 입사일 확인 관련 상태
  const [startDateEdit, setStartDateEdit] = useState<string>('');
  const [startDateConfirming, setStartDateConfirming] = useState(false);

  // 평판조회 동의서 관련 상태
  const [consent, setConsent] = useState<any>(null);

  // 온보딩 서류 관련 상태
  const [onboarding, setOnboarding] = useState<any>(null);
  const [onboardingLoading, setOnboardingLoading] = useState(false);
  const [uploadingDoc, setUploadingDoc] = useState<string | null>(null);
  const fileInputRefs = useRef<Record<string, HTMLInputElement | null>>({});

  useEffect(() => {
    if (isOffer) {
      setOfferLoading(true);
      offerApi.getMyOffer()
        .then((o: any) => { setOffer(o); setStartDateEdit(o?.start_date || ''); })
        .catch(() => {})
        .finally(() => setOfferLoading(false));
      // 평판조회 동의서 조회
      referenceConsentApi.getMyConsent()
        .then(setConsent)
        .catch(() => {});
    }
    // 희망 연봉 초기값
    if (applicant?.requested_salary) {
      setRequestedSalary(String(applicant.requested_salary));
      setSalarySaved(true);
    }
  }, [isOffer, applicant]);

  useEffect(() => {
    if (isHired) {
      setOnboardingLoading(true);
      onboardingApi.getMyOnboarding()
        .then(setOnboarding)
        .catch(() => {})
        .finally(() => setOnboardingLoading(false));
    }
  }, [isHired]);

  const handleDocUpload = async (docType: string, file: File) => {
    setUploadingDoc(docType);
    try {
      const updated = await onboardingApi.uploadDocument(docType, file);
      setOnboarding(updated);
      toast.success('서류가 업로드되었습니다.');
    } catch (err: any) {
      toast.error(err.response?.data?.message || '업로드에 실패했습니다.');
    }
    setUploadingDoc(null);
  };

  const handleStartDateConfirm = async () => {
    if (!startDateEdit) {
      toast.error('입사 예정일을 선택해주세요.');
      return;
    }
    setStartDateConfirming(true);
    try {
      const updated = await offerApi.confirmStartDate(startDateEdit);
      setOffer(updated);
      toast.success(startDateEdit !== offer?.start_date
        ? '입사 예정일이 변경 및 확인되었습니다.'
        : '입사 예정일이 확인되었습니다.');
      onUpdate?.();
    } catch (err: any) {
      toast.error(err.response?.data?.message || '입사일 확인에 실패했습니다.');
    }
    setStartDateConfirming(false);
  };

  const handleSalarySubmit = async () => {
    const val = Number(requestedSalary);
    if (!val || val <= 0) {
      toast.error('희망 연봉을 입력해주세요. (만원 단위)');
      return;
    }
    setSalarySubmitting(true);
    try {
      await offerApi.updateRequestedSalary(val);
      setSalarySaved(true);
      toast.success('희망 연봉이 저장되었습니다.');
      onUpdate?.();
    } catch (err: any) {
      toast.error(err.response?.data?.message || '저장에 실패했습니다.');
    }
    setSalarySubmitting(false);
  };

  const completedCount = onboarding
    ? DOCUMENT_LIST.filter((doc) => onboarding[doc.key]).length
    : 0;

  const handleAccept = async () => {
    const ok = await confirm({
      title: '오퍼를 수락하시겠습니까?',
      message: '수락 후 입사 준비 절차가 시작됩니다.',
      confirmText: '수락',
    });
    if (!ok) return;
    setSubmitting(true);
    try {
      await offerApi.acceptOffer();
      toast.success('오퍼를 수락하였습니다. 축하합니다!');
      onUpdate?.();
    } catch (err: any) {
      toast.error(err.response?.data?.message || '오퍼 수락에 실패했습니다.');
    }
    setSubmitting(false);
  };

  const handleReject = async () => {
    const ok = await confirm({
      title: '정말 오퍼를 거절하시겠습니까?',
      message: '담당자가 검토 후 새로운 제안을 드릴 수 있습니다.',
      confirmText: '거절',
      variant: 'danger',
    });
    if (!ok) return;
    setSubmitting(true);
    try {
      await offerApi.rejectOffer();
      toast.success('오퍼를 거절하였습니다.');
      onUpdate?.();
    } catch (err: any) {
      toast.error(err.response?.data?.message || '오퍼 거절에 실패했습니다.');
    }
    setSubmitting(false);
  };

  const formatSalary = (val: number | string | null | undefined) =>
    val ? Math.round(Number(val) / 10000).toLocaleString('ko-KR') + '만원' : '-';

  return (
    <div className="min-h-screen bg-slate-100">
      <header className="bg-white border-b border-slate-200 sticky top-0 z-50">
        <div className="max-w-3xl mx-auto px-4 py-3 flex items-center gap-3">
          <button
            onClick={() => navigate('/')}
            className="text-slate-400 hover:text-slate-600"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </button>
          <h1 className="text-base font-semibold text-slate-800">최종 결과</h1>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 py-6 space-y-6">
        <div className="bg-white rounded-xl border border-slate-200 p-6">
          {isHired ? (
            // 입사 확정 + 서류 제출
            <div>
              <div className="text-center py-8">
                <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <svg className="w-10 h-10 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <h2 className="text-xl font-semibold text-slate-800 mb-2">축하합니다!</h2>
                <p className="text-slate-600">GRK Career Management 입사가 확정되었습니다.</p>
              </div>

              {/* 입사 준비 서류 섹션 */}
              <div className="border-t border-slate-200 pt-6 mt-2">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-base font-semibold text-slate-800">입사 준비 서류</h3>
                  <span className="text-sm font-medium text-indigo-600 bg-indigo-50 px-3 py-1 rounded-full">
                    {completedCount}/9 완료
                  </span>
                </div>

                {/* 진행률 바 */}
                <div className="w-full bg-slate-200 rounded-full h-2 mb-6">
                  <div
                    className="bg-green-500 h-2 rounded-full transition-all duration-300"
                    style={{ width: `${(completedCount / 9) * 100}%` }}
                  />
                </div>

                {onboardingLoading ? (
                  <div className="text-center py-4">
                    <p className="text-sm text-slate-500">서류 현황을 불러오는 중...</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {DOCUMENT_LIST.map((doc, idx) => {
                      const isUploaded = onboarding && onboarding[doc.key];
                      const isUploading = uploadingDoc === doc.key;
                      const fileName = isUploaded ? onboarding[doc.key].split('/').pop() : null;

                      return (
                        <div
                          key={doc.key}
                          className={`p-4 rounded-lg border ${
                            isUploaded
                              ? 'bg-green-50 border-green-200'
                              : 'bg-white border-slate-200'
                          }`}
                        >
                          <div className="flex items-start justify-between gap-3">
                            <div className="flex items-start gap-3 min-w-0">
                              {/* 번호/체크 아이콘 */}
                              <div className={`w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5 ${
                                isUploaded ? 'bg-green-500' : 'bg-slate-200'
                              }`}>
                                {isUploaded ? (
                                  <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
                                  </svg>
                                ) : (
                                  <span className="text-xs font-medium text-slate-500">{idx + 1}</span>
                                )}
                              </div>
                              <div className="min-w-0">
                                <p className="text-sm font-medium text-slate-800">{doc.label}</p>
                                <p className="text-xs text-slate-500 mt-0.5">{doc.desc}</p>
                                <p className="text-xs text-slate-400 mt-0.5">허용 형식: {doc.formats}</p>
                                {isUploaded && fileName && (
                                  <p className="text-xs text-green-600 mt-1 truncate">제출완료: {fileName}</p>
                                )}
                              </div>
                            </div>
                            <div className="flex-shrink-0">
                              <input
                                type="file"
                                accept={doc.accept}
                                ref={(el) => { fileInputRefs.current[doc.key] = el; }}
                                onChange={(e) => {
                                  const file = e.target.files?.[0];
                                  if (file) handleDocUpload(doc.key, file);
                                  e.target.value = '';
                                }}
                                className="hidden"
                              />
                              <button
                                onClick={() => fileInputRefs.current[doc.key]?.click()}
                                disabled={isUploading}
                                className={`px-3 py-1.5 text-xs font-medium rounded-lg transition-colors ${
                                  isUploaded
                                    ? 'border border-green-300 text-green-700 hover:bg-green-100'
                                    : 'bg-indigo-600 text-white hover:bg-indigo-700'
                                } disabled:opacity-50`}
                              >
                                {isUploading ? '업로드 중...' : isUploaded ? '재업로드' : '파일 선택'}
                              </button>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          ) : isOffer ? (
            // 오퍼 단계
            offerLoading ? (
              <div className="text-center py-8">
                <p className="text-sm text-slate-500">오퍼 정보를 불러오는 중...</p>
              </div>
            ) : offer ? (
              <div className="space-y-6">
                {/* 축하 헤더 */}
                <div className="text-center">
                  <div className="w-20 h-20 bg-indigo-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <svg className="w-10 h-10 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z" />
                    </svg>
                  </div>
                  <h2 className="text-xl font-semibold text-slate-800 mb-2">최종 합격을 축하드립니다!</h2>
                  <p className="text-slate-600">아래 오퍼 내용을 확인하시고 수락 여부를 결정해주세요.</p>
                </div>

                {/* 평가 결과 요약 */}
                {offer.score_summary?.total_weighted && (
                  <div className="bg-slate-50 rounded-lg p-4">
                    <h3 className="text-xs font-semibold text-indigo-600 uppercase tracking-wide mb-3">평가 결과</h3>
                    <div className="space-y-2">
                      {offer.score_summary.ai?.total != null && (
                        <div className="flex justify-between py-2 border-b border-slate-200">
                          <span className="text-sm text-slate-500">AI 서류심사</span>
                          <span className="text-sm font-medium text-slate-800">{offer.score_summary.ai.total}점</span>
                        </div>
                      )}
                      {offer.score_summary.written?.logic?.score != null && (
                        <div className="flex justify-between py-2 border-b border-slate-200">
                          <span className="text-sm text-slate-500">서면평가 (논리력)</span>
                          <span className="text-sm font-medium text-slate-800">{offer.score_summary.written.logic.score}점</span>
                        </div>
                      )}
                      {offer.score_summary.written?.case?.score != null && (
                        <div className="flex justify-between py-2 border-b border-slate-200">
                          <span className="text-sm text-slate-500">서면평가 (케이스스터디)</span>
                          <span className="text-sm font-medium text-slate-800">{offer.score_summary.written.case.score}점</span>
                        </div>
                      )}
                      {offer.score_summary.interview?.score != null && (
                        <div className="flex justify-between py-2 border-b border-slate-200">
                          <span className="text-sm text-slate-500">면접</span>
                          <span className="text-sm font-medium text-slate-800">{offer.score_summary.interview.score}점</span>
                        </div>
                      )}
                      <div className="flex justify-between py-3 bg-indigo-50 rounded-lg px-3 -mx-1">
                        <span className="text-sm font-semibold text-indigo-700">종합 점수</span>
                        <span className="text-base font-bold text-indigo-700">
                          {offer.score_summary.total_weighted}점
                          {offer.percentile ? ` · 상위 ${offer.percentile}%` : ''}
                        </span>
                      </div>
                    </div>
                  </div>
                )}

                {/* 포지션 */}
                <div className="bg-slate-50 rounded-lg p-4">
                  <h3 className="text-xs font-semibold text-indigo-600 uppercase tracking-wide mb-3">포지션</h3>
                  <p className="text-base font-medium text-slate-800">{offer.position_title || '-'}</p>
                  {offer.position_duties && (
                    <p className="text-sm text-slate-600 mt-2 whitespace-pre-wrap leading-relaxed">{offer.position_duties}</p>
                  )}
                </div>

                {/* 급여 테이블 */}
                <div className="bg-slate-50 rounded-lg p-4">
                  <h3 className="text-xs font-semibold text-indigo-600 uppercase tracking-wide mb-3">보수 조건</h3>
                  <div className="space-y-2">
                    <div className="flex justify-between py-2 border-b border-slate-200">
                      <span className="text-sm text-slate-500">기본급</span>
                      <span className="text-sm font-medium text-slate-800">{formatSalary(offer.base_salary)}</span>
                    </div>
                    <div className="flex justify-between py-2 border-b border-slate-200">
                      <span className="text-sm text-slate-500">상여금/성과급</span>
                      <span className="text-sm font-medium text-slate-800">{formatSalary(offer.bonus)}</span>
                    </div>
                    <div className="flex justify-between py-2 border-b border-slate-200">
                      <span className="text-sm text-slate-500">복리후생/수당</span>
                      <span className="text-sm font-medium text-slate-800">{formatSalary(offer.benefits)}</span>
                    </div>
                    <div className="flex justify-between py-3 bg-green-50 rounded-lg px-3 -mx-1">
                      <span className="text-sm font-semibold text-green-700">연봉 총액</span>
                      <span className="text-base font-bold text-green-700">{formatSalary(offer.total_salary)}</span>
                    </div>
                  </div>
                </div>

                {/* 근무 조건 */}
                <div className="bg-slate-50 rounded-lg p-4">
                  <h3 className="text-xs font-semibold text-indigo-600 uppercase tracking-wide mb-3">근무 조건</h3>
                  <div className="space-y-4">
                    <div>
                      <p className="text-xs text-slate-400 mb-1">입사 예정일</p>
                      {offer.start_date_confirmed ? (
                        <div className="flex items-center gap-2">
                          <p className="text-sm font-medium text-slate-800">{offer.start_date || '-'}</p>
                          <span className="text-xs text-green-600 bg-green-50 px-2 py-0.5 rounded-full">확인됨</span>
                        </div>
                      ) : offer.status === 'SENT' ? (
                        <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                          <div className="flex items-center gap-2 mb-2">
                            <input
                              type="date"
                              value={startDateEdit}
                              onChange={(e) => setStartDateEdit(e.target.value)}
                              className="px-3 py-1.5 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                            />
                            <button
                              onClick={handleStartDateConfirm}
                              disabled={startDateConfirming}
                              className="px-3 py-1.5 text-xs font-medium bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50"
                            >
                              {startDateConfirming ? '확인 중...' : '입사일 확인'}
                            </button>
                          </div>
                          <p className="text-xs text-amber-700">입사 예정일을 확인하거나 희망하는 날짜로 수정해주세요.</p>
                        </div>
                      ) : (
                        <p className="text-sm font-medium text-slate-800">{offer.start_date || '-'}</p>
                      )}
                    </div>
                    <div>
                      <p className="text-xs text-slate-400 mb-1">근무지</p>
                      <p className="text-sm font-medium text-slate-800">{offer.work_location || '-'}</p>
                    </div>
                  </div>
                </div>

                {/* 개인 메시지 */}
                {offer.personal_message && (
                  <div className="bg-purple-50 rounded-lg p-4 border border-purple-100">
                    <h3 className="text-xs font-semibold text-purple-600 uppercase tracking-wide mb-2">메시지</h3>
                    <p className="text-sm text-purple-800 whitespace-pre-wrap leading-relaxed">{offer.personal_message}</p>
                  </div>
                )}

                {/* 수락/거절 버튼 — SENT 상태에서만 표시 */}
                {offer.status === 'SENT' && (
                  <div>
                    {!offer.start_date_confirmed && (
                      <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-3 text-center">
                        <p className="text-sm font-medium text-amber-800">입사 예정일을 먼저 확인해주세요.</p>
                        <p className="text-xs text-amber-600 mt-1">위 근무 조건에서 입사일을 확인해야 오퍼 수락이 가능합니다.</p>
                      </div>
                    )}
                    {consent?.requested && !consent?.signed && (
                      <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-3 text-center">
                        <p className="text-sm font-medium text-amber-800">평판조회 동의서를 먼저 작성해주세요.</p>
                        <p className="text-xs text-amber-600 mt-1">아래 동의서를 작성해야 오퍼 수락이 가능합니다.</p>
                      </div>
                    )}
                    <div className="flex gap-3 pt-2">
                      <button onClick={handleReject} disabled={submitting}
                        className="flex-1 px-4 py-3 text-sm font-medium border-2 border-red-300 text-red-600 rounded-lg hover:bg-red-50 disabled:opacity-50 transition-colors">
                        {submitting ? '처리 중...' : '오퍼 거절'}
                      </button>
                      <button onClick={handleAccept} disabled={submitting || !offer.start_date_confirmed || (consent?.requested && !consent?.signed)}
                        className={`flex-1 px-4 py-3 text-sm font-medium rounded-lg transition-colors ${
                          !offer.start_date_confirmed || (consent?.requested && !consent?.signed)
                            ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                            : 'bg-green-600 text-white hover:bg-green-700 disabled:opacity-50'
                        }`}>
                        {submitting ? '처리 중...' : '오퍼 수락'}
                      </button>
                    </div>
                  </div>
                )}
                {offer.status === 'NEGOTIATING' && (
                  <div className="bg-orange-50 border border-orange-200 rounded-lg p-4 mt-2 text-center">
                    <p className="text-sm font-medium text-orange-800">오퍼를 거절하였습니다.</p>
                    <p className="text-xs text-orange-600 mt-1">담당자가 검토 후 새로운 제안을 드릴 수 있습니다.</p>
                  </div>
                )}
                {offer.status === 'ACCEPTED' && (
                  <div className="bg-green-50 border border-green-200 rounded-lg p-4 mt-2 text-center">
                    <p className="text-sm font-medium text-green-800">오퍼를 수락하였습니다.</p>
                  </div>
                )}
                {offer.status === 'REJECTED' && (
                  <div className="bg-red-50 border border-red-200 rounded-lg p-4 mt-2 text-center">
                    <p className="text-sm font-medium text-red-800">오퍼를 거절하였습니다.</p>
                  </div>
                )}

                {/* 평판조회 동의서 */}
                {consent?.requested && (
                  <div className="mt-8 pt-6 border-t border-slate-200">
                    <h3 className="text-lg font-semibold text-slate-800 mb-4">📋 평판조회 동의서</h3>
                    <ReferenceConsentForm
                      isSubmitted={consent?.signed || false}
                      signedAt={consent?.signed_at}
                      onSubmit={async (data: any) => {
                        await referenceConsentApi.submitConsent(data);
                        toast.success('평판조회 동의서가 제출되었습니다.');
                        const updated = await referenceConsentApi.getMyConsent();
                        setConsent(updated);
                      }}
                    />
                  </div>
                )}
              </div>
            ) : (
              // 오퍼 미생성 — 합격 안내 + 희망 연봉 입력
              <div className="space-y-6">
                <div className="text-center">
                  <div className="w-20 h-20 bg-indigo-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <svg className="w-10 h-10 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z" />
                    </svg>
                  </div>
                  <h2 className="text-xl font-semibold text-slate-800 mb-2">최종 합격을 축하드립니다!</h2>
                  <p className="text-slate-600">처우 제안을 준비 중입니다. 아래에 희망 연봉을 입력해주세요.</p>
                </div>

                {/* 희망 연봉 입력 */}
                <div className="bg-amber-50 rounded-lg p-5 border border-amber-200">
                  <h3 className="text-sm font-semibold text-amber-800 mb-3">희망 연봉</h3>
                  <div className="flex items-center gap-3">
                    <div className="relative flex-1">
                      <input
                        type="number"
                        value={requestedSalary}
                        onChange={(e) => { setRequestedSalary(e.target.value); setSalarySaved(false); }}
                        placeholder="만원 단위로 입력"
                        className="w-full px-4 py-3 border border-amber-300 rounded-lg text-sm focus:ring-2 focus:ring-amber-400 focus:border-amber-400 outline-none"
                        disabled={salarySubmitting}
                      />
                      <span className="absolute right-4 top-1/2 -translate-y-1/2 text-sm text-slate-400">만원</span>
                    </div>
                    <button
                      onClick={handleSalarySubmit}
                      disabled={salarySubmitting || salarySaved}
                      className={`px-5 py-3 text-sm font-medium rounded-lg transition-colors ${
                        salarySaved
                          ? 'bg-green-100 text-green-700 border border-green-300'
                          : 'bg-amber-600 text-white hover:bg-amber-700'
                      } disabled:opacity-50`}
                    >
                      {salarySubmitting ? '저장 중...' : salarySaved ? '저장됨' : '저장'}
                    </button>
                  </div>
                  {salarySaved && (
                    <p className="text-xs text-green-600 mt-2">희망 연봉이 저장되었습니다. 처우 제안 시 참고됩니다.</p>
                  )}
                </div>

                <div className="p-4 bg-indigo-50 rounded-lg border border-indigo-200 text-center">
                  <p className="text-sm text-indigo-800">
                    처우 제안서를 이메일로 발송드릴 예정입니다.<br/>
                    잠시만 기다려주시기 바랍니다.
                  </p>
                </div>
              </div>
            )
          ) : isRejected ? (
            // 불합격
            <div className="text-center py-8">
              <div className="w-20 h-20 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-10 h-10 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" />
                </svg>
              </div>
              <h2 className="text-xl font-semibold text-slate-800 mb-2">
                {isDocRejected ? '서류 심사에서 불합격되었습니다'
                  : isWrittenRejected ? '서면 평가에서 불합격되었습니다'
                  : '채용 프로세스가 종료되었습니다'}
              </h2>
              <p className="text-slate-500">
                {isDocRejected ? (
                  <>서류 심사 결과 아쉽게도 불합격되었습니다.<br/>지원해주셔서 감사합니다.</>
                ) : isWrittenRejected ? (
                  <>서면 평가 결과 아쉽게도 불합격되었습니다.<br/>지원해주셔서 감사합니다.</>
                ) : (
                  <>지원해주셔서 감사합니다.<br/>다음 기회에 다시 만나 뵙기를 바랍니다.</>
                )}
              </p>
            </div>
          ) : (
            // 결과 대기 중
            <div className="text-center py-8">
              <div className="w-20 h-20 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-10 h-10 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <h2 className="text-lg font-semibold text-slate-800 mb-2">아직 최종 결과 단계가 아닙니다</h2>
              <p className="text-slate-500">
                면접까지 완료 후 최종 결과를 확인하실 수 있습니다.
              </p>
            </div>
          )}
        </div>

        {/* 문의 안내 */}
        <div className="bg-slate-50 rounded-xl p-4 text-center">
          <p className="text-sm text-slate-500">
            문의사항: <span className="font-medium text-slate-700">sungbae@grkcon.com</span>
          </p>
        </div>
      </main>
    </div>
  );
};

export default ResultPage;
